import { useCallback, useMemo } from "react";
import { useMutation } from "@tanstack/react-query";
import { useQuery } from "@apollo/client/react";
import { toast } from "sonner";
import type { Message, MessageEntity } from "@/lib/core/crm/types";
import type { EntityHooks } from "@/components/shared/UniversalCRMTable/types";
import { GetCommunicationsDocument } from "@/src/gql/graphql";
import type { GetCommunicationsQuery } from "@/src/gql/graphql";
import { useHydratedCommunicationBody } from "@/lib/hooks/use-hydrated-communication-body";
import { mapCommunicationToMessage } from "@/lib/core/crm/messages/communication-to-message";
import {
  EMPTY_INFINITE_RESULT,
  EMPTY_PAGINATED_RESULT,
  useStableMutationResult,
} from "@/lib/hooks/api/shared/stable-entity-hook-results";

/**
 * Hook for fetching a single message for the /crm/messages/[id] detail page.
 *
 * Own-infra replacement for the retired Algolia `emails_search` / `hub` reads:
 * the [id] is a `communications` UUID, loaded by id over pg_graphql (RLS-scoped)
 * and mapped onto the Message shape. The full body is hydrated from S3-or-inline
 * via GET /api/communications/[id]/body when the row has been offloaded.
 */
export function useMessageSingle(messageId: string, options?: { enabled?: boolean }) {
  const result = useQuery<GetCommunicationsQuery>(GetCommunicationsDocument, {
    variables: { id: messageId },
    errorPolicy: "all",
    skip: !messageId || options?.enabled === false,
  });

  const node = result.data?.communicationsCollection?.edges?.[0]?.node;

  const { body: resolvedBody } = useHydratedCommunicationBody({
    communicationId: node?.id,
    inlineBody: node?.body,
    bodyS3Key: node?.body_s3_key,
    previewSnippet: node?.body_preview,
  });

  const message: Message | undefined = node
    ? mapCommunicationToMessage(node, resolvedBody)
    : undefined;

  return useMemo(
    () => ({
      data: message as MessageEntity | undefined,
      isLoading: result.loading,
      error: result.error ? new Error(result.error.message || String(result.error)) : null,
      refetch: () => {
        void result.refetch();
      },
    }),
    [message, result]
  );
}

/**
 * Mailbox mutations are NOT available through the CRM entity-hook contract.
 *
 * These three used to POST/PATCH/DELETE `/api/connections/messages/message/...` — a path
 * whose `[provider]` segment resolved to the literal "message", and whose route
 * only exports GET. Every call 405'd (or fell through to the GET handler),
 * surfacing as a generic "Failed to ..." toast. `validate:api-routes` flags
 * them as caller/route method mismatches.
 *
 * There is no create/update/delete endpoint to point them at, by design:
 * sending mail goes through the compose/send path, and organize operations
 * (read/unread, flag, move, archive) go through the per-message `actions`
 * endpoint. Deleting mail is deliberately not offered at all (see
 * AI_EMAIL_MANAGEMENT). So these reject immediately with an honest reason
 * instead of issuing a request that cannot succeed.
 */
export function useMessageCreate() {
  const mutation = useMutation({
    mutationFn: (data: Partial<Message> & { id?: string }): Promise<never> => {
      void data;
      return Promise.reject(
        new Error("Creating a message is not supported here — send mail through the compose flow instead.")
      );
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  return useStableMutationResult(mutation.mutateAsync, mutation.isPending);
}

export function useMessageUpdate() {
  const mutation = useMutation({
    mutationFn: (data: Partial<Message> & { id: string }): Promise<never> => {
      void data;
      return Promise.reject(
        new Error("Editing a message is not supported here — use the per-message actions endpoint for read/unread, flag, or move.")
      );
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  return useStableMutationResult(mutation.mutateAsync, mutation.isPending);
}

export function useMessageDelete() {
  const mutation = useMutation({
    mutationFn: (messageId: string): Promise<never> => {
      void messageId;
      return Promise.reject(
        new Error("Deleting a message is not supported here — mailbox deletion is intentionally not offered.")
      );
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  return useStableMutationResult(mutation.mutateAsync, mutation.isPending);
}

/**
 * Placeholder hooks for paginated and infinite queries. Both hand back the
 * SHARED module-level constant so the entity-hooks object never carries a
 * freshly-allocated result into `useBuildActionContext`'s memo deps.
 */
export function useMessagePaginated() {
  return EMPTY_PAGINATED_RESULT;
}

export function useMessageInfinite() {
  return EMPTY_INFINITE_RESULT;
}

/**
 * Main message hooks factory.
 *
 * Allocated ONCE at module scope: every member is a module-level function
 * reference, so the object closes over nothing render-scoped. See
 * `lib/hooks/api/shared/contacts-graphql.ts`.
 */
const MESSAGE_ENTITY_HOOKS: EntityHooks<MessageEntity> = {
  usePaginated: useMessagePaginated,
  useInfinite: useMessageInfinite,
  useSingle: useMessageSingle,
  useCreate: useMessageCreate,
  useUpdate: useMessageUpdate,
  useDelete: useMessageDelete,
};

export function useMessageHooks(): EntityHooks<MessageEntity> {
  return MESSAGE_ENTITY_HOOKS;
}
